import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
// Import all thunks from modular commentThunks folder
import {
  hydrateCommentsFromCache,
  fetchPostComments,
  createComment,
  updateComment,
  removeComment,
} from "./commentThunks/index.js";
import { toggleCommentLikeAsync } from "./commentLikesSlice";
import { createReply, removeReply } from "./repliesSlice/thunks.js";

// --- Slice ---
const initialState = {
  items: {}, // { postId: [comments] }
  loading: false,
  loadingActions: {},
  error: null,
  activeComment: null,
  pagination: {}, // { postId: { nextCursor, hasMore } }
  displayCount: {}, // { postId: number }
  lastFetchedAt: {},
  editRollback: {}, // { [commentId]: previousText }
  deleteRollback: {}, // { [commentId]: { text, deletedAt } } — for removeComment rollback
};

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    clearComments: (state) => {
      state.items = {};
      state.activeComment = null;
      state.pagination = {};
      state.loadingActions = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    setActiveComment: (state, action) => {
      state.activeComment = action.payload;
    },
    setDisplayCount: (state, action) => {
      const { postId, count } = action.payload;
      if (postId && typeof count === "number") {
        state.displayCount[postId] = count;
      }
    },
    setComments: (state, action) => {
      const { postId, comments } = action.payload;
      state.items[postId] = comments;
    },
    updateReplyCount: (state, action) => {
      const { commentId, postId, newReplyCount } = action.payload;
      const comment = state.items[postId]?.find((c) => c.id === commentId);
      if (comment) {
        comment.replyCount = newReplyCount;
      }
    },
    patchCommentLikeCount: (state, action) => {
      const { postId, commentId, likes } = action.payload || {};
      if (!postId || !commentId || typeof likes !== "number") return;
      const comment = state.items[postId]?.find((c) => c.id === commentId);
      if (comment) comment.likes = likes;
    },
  },
  extraReducers: (builder) => {
    builder
      // Hydrate from cache
      .addCase(hydrateCommentsFromCache.fulfilled, (state, action) => {
        const { postId, comments } = action.payload;
        if (!state.items[postId]) {
          state.items[postId] = comments;
        }
      })

      // Fetch comments
      .addCase(fetchPostComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPostComments.fulfilled, (state, action) => {
        const { postId, comments, nextCursor, hasMore, skipped } = action.payload;

        if (!skipped) {
          if (!state.items[postId]) {
            state.items[postId] = comments;
          } else {
            // Merge, avoiding duplicates
            const existingIds = new Set(state.items[postId].map((c) => c.id));
            const newComments = comments.filter((c) => !existingIds.has(c.id));
            state.items[postId] = [...state.items[postId], ...newComments];
          }
          state.lastFetchedAt[postId] = Date.now();
        }

        state.pagination[postId] = { nextCursor, hasMore };
        state.loading = false;
      })
      .addCase(fetchPostComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch comments";
      })

      // Create comment (optimistic)
      .addCase(createComment.pending, (state, action) => {
        const { postId, comment, tempId } = action.meta.arg;
        const optimistic = {
          ...comment,
          id: tempId,
          isOptimistic: true,
          timestamp: Date.now(),
        };
        if (!state.items[postId]) state.items[postId] = [];
        state.items[postId].unshift(optimistic);
        state.loadingActions[tempId] = true;
      })
      .addCase(createComment.fulfilled, (state, action) => {
        const { postId, comment, tempId } = action.payload;
        const list = state.items[postId] || [];
        const idx = list.findIndex((c) => c.id === tempId || c.id === comment.id);
        if (idx >= 0) {
          list[idx] = { ...comment, isOptimistic: false };
        } else {
          list.unshift(comment);
        }
        delete state.loadingActions[tempId];
        state.lastFetchedAt[postId] = Date.now();
      })
      .addCase(createComment.rejected, (state, action) => {
        const { tempId, postId } = action.meta.arg;
        // Remove optimistic comment
        if (state.items[postId]) {
          state.items[postId] = state.items[postId].filter(
            (c) => c.id !== tempId,
          );
        }
        delete state.loadingActions[tempId];
        toast.error(action.payload?.message || "Failed to create comment");
      })

      // Update comment
      .addCase(updateComment.pending, (state, action) => {
        const { commentId, text } = action.meta.arg;
        // Store rollback
        for (const postId of Object.keys(state.items)) {
          const c = state.items[postId]?.find((x) => x.id === commentId);
          if (c) {
            state.editRollback[commentId] = c.text;
            c.text = text;
            break;
          }
        }
        state.loadingActions[`update-${commentId}`] = true;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        const { commentId, postId, updatedAt } = action.payload;
        const c = state.items[postId]?.find((x) => x.id === commentId);
        if (c) {
          c.updatedAt = updatedAt;
        }
        delete state.editRollback[commentId];
        delete state.loadingActions[`update-${commentId}`];
      })
      .addCase(updateComment.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        // Rollback
        for (const postId of Object.keys(state.items)) {
          const c = state.items[postId]?.find((x) => x.id === commentId);
          if (c && state.editRollback[commentId]) {
            c.text = state.editRollback[commentId];
            break;
          }
        }
        delete state.editRollback[commentId];
        delete state.loadingActions[`update-${commentId}`];
        toast.error(action.payload || "Failed to update comment");
      })

      // Remove comment (soft delete)
      .addCase(removeComment.pending, (state, action) => {
        const { commentId } = action.meta.arg;
        for (const postId of Object.keys(state.items)) {
          const c = state.items[postId]?.find((x) => x.id === commentId);
          if (c) {
            // Save rollback snapshot BEFORE mutating (BUG 1 fix)
            state.deleteRollback[commentId] = { text: c.text, deletedAt: c.deletedAt };
            c.deletedAt = new Date().toISOString();
            c.text = "[Comment removed]";
            break;
          }
        }
        state.loadingActions[`delete-${commentId}`] = true;
      })
      .addCase(removeComment.fulfilled, (state, action) => {
        const { commentId } = action.payload;
        delete state.deleteRollback[commentId];
        delete state.loadingActions[`delete-${commentId}`];
      })
      .addCase(removeComment.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        // Restore pre-deletion state from snapshot (BUG 1 fix)
        const rollback = state.deleteRollback[commentId];
        if (rollback) {
          for (const postId of Object.keys(state.items)) {
            const c = state.items[postId]?.find((x) => x.id === commentId);
            if (c) {
              c.text = rollback.text;
              if (rollback.deletedAt !== undefined) {
                c.deletedAt = rollback.deletedAt;
              } else {
                delete c.deletedAt;
              }
              break;
            }
          }
          delete state.deleteRollback[commentId];
        }
        delete state.loadingActions[`delete-${commentId}`];
        toast.error(action.payload || "Failed to delete comment");
      })

      // Cross-slice sync: when a comment like is toggled, update the comment's likes count
      // in state.comments.items[postId] so the UI count display matches the like state.
      // The heart icon is driven by commentLikesSlice.likedCommentIds; the count is driven
      // by comment.likes here.
      .addCase(toggleCommentLikeAsync.fulfilled, (state, action) => {
        const { postId, commentId } = action.meta.arg;
        const { likes } = action.payload;
        if (typeof likes !== "number" || !state.items[postId]) return;
        const comment = state.items[postId].find((c) => c.id === commentId);
        if (comment) {
          comment.likes = likes;
        }
      })
      // Cross-slice sync: increment replyCount when a new reply is created
      .addCase(createReply.fulfilled, (state, action) => {
        const { postId, commentId } = action.payload;
        if (!postId || !commentId || !state.items[postId]) return;
        const comment = state.items[postId].find((c) => c.id === commentId);
        if (comment) {
          comment.replyCount = (comment.replyCount || 0) + 1;
        }
      })
      // Cross-slice sync: decrement replyCount when a reply is deleted
      .addCase(removeReply.fulfilled, (state, action) => {
        const { postId, commentId } = action.payload;
        if (!postId || !commentId || !state.items[postId]) return;
        const comment = state.items[postId].find((c) => c.id === commentId);
        if (comment && typeof comment.replyCount === "number" && comment.replyCount > 0) {
          comment.replyCount -= 1;
        }
      });
  },
});

export const {
  clearComments,
  clearError,
  setActiveComment,
  setDisplayCount,
  setComments,
  updateReplyCount,
  patchCommentLikeCount,
} = commentsSlice.actions;

// Re-export all thunks for backward compatibility
export {
  hydrateCommentsFromCache,
  fetchPostComments,
  createComment,
  updateComment,
  removeComment,
};

export default commentsSlice.reducer;
